import { useEffect, useState } from 'react';
import { phraseConsultation } from '../../api/useConsultations';
import { dateSuisse } from '../../lib/dates';
import { articlesAReapprovisionner } from '../../lib/valeur-stock';
import { decideConge } from '../equipe/decisionConge';
import { Shell } from '../../components/layout/Shell.jsx';
import { LienCertificat, ProposerDate } from '../equipe/ProposerDate.jsx';
import { ListeSignaux } from '../ouvrier/SignauxPage.jsx';

import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { ACCESS_ROLE } from '../../lib/access';
import { nomComplet } from '../../lib/acces';
import { I18N, LANG, tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { termesDeLaPiece } from '../../lib/terminologie';
import { alertesFacturation } from '../../lib/suivi-affaires';
import {
  attestationsARenouveler,
  etatAttestation,
  libelleAttestation,
} from '../../lib/attestations';
import { isoVersDate } from '../../api/mappers';
import { DEVISE, fmt } from '../../lib/currency';

export function NotificationsPage({
  onNav,
  soc,
  estMixte,
  univers,
  setUnivers,
  devis,
  factures,
  chantiers,
  clients,
  conges,
  setConges,
  employes,
  stock,
  commandes,
  consultations,
  onConsultationsVues,
  attestations,
  // La cloche personnelle d'un compte du bureau rattaché à une fiche.
  signaux,
}) {
  const [proposeC, setProposeC] = useState(null);
  // Les ouvertures non lues à l'arrivée sur la page restent marquées
  // « nouveau » le temps de la visite ; la cloche, elle, s'éteint aussitôt.
  const [nouvellesVues] = useState(
    () => new Set((consultations || []).filter((c) => !c.lu).map((c) => c.id)),
  );
  useEffect(() => {
    if (onConsultationsVues && (consultations || []).some((c) => !c.lu)) onConsultationsVues();
  }, [consultations, onConsultationsVues]);
  const quand = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const jour = d.toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const heure = d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
    return trf('{0} à {1}', jour, heure);
  };
  const trLocal = (s) => {
    const _p = soc?.pays;
    const _lg = !_p || _p === 'CH' ? 'fr-CH' : soc?.lang || LANG;
    return (I18N[_lg] && I18N[_lg][s]) || (I18N[LANG] && I18N[LANG][s]) || s;
  };
  const nameOf = (id) => {
    const c = (clients || []).find((x) => x.id === id);
    return (c && c.nom) || 'client';
  };
  const empName = (id) => {
    const e = (employes || []).find((x) => x.id === id);
    return (e && e.nom) || 'Un employé';
  };
  const fd = (iso) => {
    if (!iso) return '';
    const z = ('' + iso).split('-');
    return z.length === 3 ? z[2] + '.' + z[1] + '.' + z[0] : iso;
  };
  const peutValider =
    ACCESS_ROLE === 'patron' || ACCESS_ROLE === 'comptable' || ACCESS_ROLE === 'secretaire';
  // La facturation qui attend : chantier fini sans facture finale, facture
  // de chantier dont l'échéance est passée.
  const alertes =
    ACCESS_ROLE === 'ouvrier' ? [] : alertesFacturation({ devis, factures, chantiers });
  // Une attestation de l'entreprise qui finit dans les 30 jours, ou déjà finie.
  const aRenouveler = ACCESS_ROLE === 'ouvrier' ? [] : attestationsARenouveler(attestations);
  // Demandes de congé en attente (affichées en priorité, avec actions)
  const demandes = (conges || []).filter((c) => c.statut === 'En attente');
  const msgs = [];
  (employes || []).forEach((e) => {
    (e.messages || []).forEach((m) => {
      if (m.de === 'employe' && !m.lu)
        msgs.push({ empId: e.id, nom: nomComplet(e), texte: m.texte, date: m.date });
    });
  });
  const setStatut = (id, st) => {
    const conge = (conges || []).find((c) => c.id === id);
    if (conge) decideConge(conge, st === 'Approuvé', setConges);
  };
  const evts = [];
  (conges || []).forEach((c) => {
    if (c.statut === 'Approuvé')
      evts.push({
        ic: '✅',
        txt:
          empName(c.empId) +
          ' — ' +
          (c.type || 'congé') +
          ' approuvé (' +
          fd(c.debut) +
          (c.fin && c.fin !== c.debut ? ' → ' + fd(c.fin) : '') +
          ')',
        date: fd(c.debut),
      });
    if (c.statut === 'Refusé')
      evts.push({
        ic: '🚫',
        txt:
          empName(c.empId) +
          ' — ' +
          (c.type || 'congé') +
          ' refusé (' +
          fd(c.debut) +
          (c.fin && c.fin !== c.debut ? ' → ' + fd(c.fin) : '') +
          ')',
        date: fd(c.debut),
      });
  });
  (devis || []).forEach((d) => {
    const termes = termesDeLaPiece(soc, d);
    const mot = termes.Mot;
    const n = d.num || d.id;
    if (d.statut === 'Accepté')
      evts.push({
        ic: '✅',
        txt: termes.accord(
          `${mot} ${n} accepté par ${nameOf(d.clientId)}`,
          `${mot} ${n} acceptée par ${nameOf(d.clientId)}`,
        ),
        date: d.signDate || d.date,
        cible: ['devis', { devisId: d.id }],
      });
    if (d.statut === 'Refusé')
      evts.push({
        ic: '❌',
        // Le motif du client, quand il l'a donné par son lien : le bureau
        // sait pourquoi avant de relancer.
        txt:
          termes.accord(
            `${mot} ${n} refusé par ${nameOf(d.clientId)}`,
            `${mot} ${n} refusée par ${nameOf(d.clientId)}`,
          ) + (d.motifRefus ? ' — « ' + d.motifRefus + ' »' : ''),
        date: d.refuseLe || d.date,
        cible: ['devis', { devisId: d.id }],
      });
    // « Encaissé » seulement quand la facture d'acompte est payée : le
    // signal s'affichait dès qu'elle était demandée.
    const acompte =
      (factures || []).find((f) => f.type === 'acompte' && String(f.devisId) === String(d.id)) ||
      (d.acompteFacture && (factures || []).find((f) => (f.num || f.id) === d.acompteFacture));
    if (acompte || d.acompteFacture)
      evts.push(
        acompte?.statut === 'Payée'
          ? {
              ic: '💰',
              txt: 'Acompte encaissé — ' + mot + ' ' + n,
              date: acompte.paidDate || d.signDate || d.date,
              cible: ['factures', { factureId: acompte.id }],
            }
          : {
              ic: '🧾',
              txt: 'Acompte demandé — ' + mot + ' ' + n,
              date: d.signDate || d.date,
              cible: acompte
                ? ['factures', { factureId: acompte.id }]
                : ['devis', { devisId: d.id }],
            },
      );
  });
  (factures || []).forEach((f) => {
    const n = f.num || f.id;
    if (f.statut === 'Payée')
      evts.push({
        ic: '🧾',
        txt: 'Facture ' + n + ' payée par ' + nameOf(f.clientId),
        date: f.datePaiement || f.date,
        cible: ['factures', { factureId: f.id }],
      });
  });
  const pk = (x) => {
    if (!x) return 0;
    const p = String(x).split('.');
    return p.length === 3 ? Number(p[2] + p[1].padStart(2, '0') + p[0].padStart(2, '0')) : 0;
  };
  evts.sort((a, b) => pk(b.date) - pk(a.date));
  const stockAlerte = articlesAReapprovisionner(stock);
  // Les commandes que les clients ont passées eux-mêmes et que le magasin n'a
  // pas encore acceptées.
  const nouvellesCommandes = (commandes || []).filter((c) => c.statut === 'Reçue');
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
      pageTitle="🔔 Notifications"
    >
      {signaux && (signaux.liste || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              color: C.muted,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              margin: '0 0 6px',
            }}
          >
            {tr('Pour moi — mon espace')}
            {signaux.nonVus > 0 ? ' · ' + trf('{0} nouveau(x)', signaux.nonVus) : ''}
          </div>
          <ListeSignaux signaux={signaux} onOuvrir={onNav} />
        </div>
      )}
      {nouvellesCommandes.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.green }}>
          <Bar
            icon="🛒"
            title={'Nouvelles commandes (' + nouvellesCommandes.length + ')'}
            color={C.green}
            sub="Passées par tes clients — à accepter"
          />
          <div style={{ padding: 13 }}>
            {nouvellesCommandes.map((c) => (
              <div
                key={c.id}
                role="button"
                onClick={() => onNav('commandes', { commandeId: c.id })}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid ' + C.border,
                  padding: '8px 0',
                  cursor: 'pointer',
                }}
              >
                <div style={{ flex: 1, marginRight: 8 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800 }}>
                    {c.num} · {c.clientNom || nameOf(c.clientId)}
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {c.demandePar ? 'par ' + c.demandePar + ' · ' : ''}
                    {c.date} · {(c.lignes || []).length} article(s)
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: C.green }}>Voir ›</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {(consultations || []).length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.navy }}>
          <Bar
            icon="👀"
            title="Consultations par tes clients"
            color={C.navy}
            sub="Chaque fois qu’un client ouvre son offre ou sa facture"
          />
          <div style={{ padding: '6px 13px 10px' }}>
            {consultations.slice(0, 20).map((c) => (
              <div
                key={c.id}
                role="button"
                // La pièce consultée elle-même, et non la liste des pièces.
                onClick={() =>
                  c.type === 'FACTURE'
                    ? onNav('factures', { factureId: c.referenceId })
                    : onNav('devis', { devisId: c.referenceId })
                }
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid ' + C.border,
                  padding: '8px 0',
                  cursor: 'pointer',
                }}
              >
                <div style={{ flex: 1, marginRight: 8, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                    {phraseConsultation(c, termesDeLaPiece(soc).mot)}
                  </div>
                  <div style={{ fontSize: 9, color: C.muted }}>{quand(c.consulteLe)}</div>
                </div>
                {nouvellesVues.has(c.id) ? (
                  <Pill label="Nouveau" color={C.navy} />
                ) : (
                  <span style={{ fontSize: 15, color: C.muted }}>›</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
      {stockAlerte.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.red }}>
          <Bar
            icon="📦"
            title={'Stock à réapprovisionner (' + stockAlerte.length + ')'}
            color={C.red}
            sub="Articles au seuil minimum ou en dessous"
          />
          <div style={{ padding: 13 }}>
            {stockAlerte.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid ' + C.border,
                  padding: '8px 0',
                }}
              >
                <div style={{ flex: 1, marginRight: 8 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700 }}>{m.nom}</div>
                  {m.ref && <div style={{ fontSize: 8, color: C.muted }}>{m.ref}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      color: (+m.stock || 0) <= 0 ? C.red : C.amber,
                    }}
                  >
                    {+m.stock || 0} {m.u}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    seuil : {+m.min || 0} {m.u}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 10 }}>
              <Btn
                label="📦 Ouvrir le stock pour réapprovisionner"
                sm
                color={C.navy}
                full
                onClick={() => onNav('stock')}
              />
            </div>
          </div>
        </Card>
      )}
      {msgs.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.navy }}>
          <Bar
            icon="💬"
            title={'Messages des employés (' + msgs.length + ')'}
            color={C.navy}
            sub="Nouveaux messages — touche pour ouvrir la conversation"
          />
          <div style={{ padding: '4px 13px 12px' }}>
            {msgs.map((m, i) => (
              <div
                key={i}
                onClick={() => onNav('equipe', { empId: m.empId, onglet: 'messages' })}
                style={{
                  borderBottom: '1px solid ' + C.border,
                  padding: '11px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 11,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: C.navy + '18',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    fontWeight: 900,
                    color: C.navy,
                    flexShrink: 0,
                  }}
                >
                  {(m.nom || '?').charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{m.nom}</div>
                    <span style={{ fontSize: 8.5, color: C.muted, flexShrink: 0 }}>
                      {dateSuisse(m.date)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: C.grey,
                      marginTop: 3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.texte}
                  </div>
                  <div style={{ fontSize: 9, color: C.navy, fontWeight: 700, marginTop: 4 }}>
                    → Répondre
                  </div>
                </div>
                <span style={{ fontSize: 16, color: C.muted, alignSelf: 'center' }}>›</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {aRenouveler.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.amber }}>
          <Bar
            icon="🏢"
            title={trf('Attestations à renouveler ({0})', aRenouveler.length)}
            color={C.amber}
          />
          <div style={{ padding: '4px 13px 8px' }}>
            {aRenouveler.map((p, i) => {
              const e = etatAttestation(p, 'conseille');
              return (
                <div
                  key={p.id}
                  role="button"
                  data-testid="alerte-attestation"
                  onClick={() => onNav('compta', { onglet: 'classeur' })}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '9px 0',
                    borderTop: i ? '1px solid ' + C.border : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 17 }}>{e.etat === 'expiree' ? '⚠️' : '⏰'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                      {tr(libelleAttestation(p.categorie, soc?.forme))}
                    </div>
                    <div style={{ fontSize: 9, color: C.muted }}>
                      {e.etat === 'expiree'
                        ? trf('Finie le {0} · touche pour ajouter la nouvelle', isoVersDate(e.fin))
                        : trf('Finit dans {0} jour(s), le {1}', e.jours, isoVersDate(e.fin))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {alertes.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.amber }}>
          <Bar icon="🧾" title={trf('Facturation à faire ({0})', alertes.length)} color={C.amber} />
          <div style={{ padding: '4px 13px 8px' }}>
            {alertes.map((a, i) => (
              <div
                key={a.genre + '-' + (a.factureId ?? a.devisId) + '-' + i}
                role="button"
                data-testid="alerte-facturation"
                onClick={() =>
                  a.genre === 'finale'
                    ? onNav('accueil')
                    : onNav(
                        'factures',
                        a.factureId != null ? { factureId: a.factureId } : undefined,
                      )
                }
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: '9px 0',
                  borderTop: i ? '1px solid ' + C.border : 'none',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 17 }}>{a.genre === 'finale' ? '⚠️' : '⏰'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                    {a.genre === 'finale'
                      ? trf(
                          '{0} : travaux finis, facture finale à faire',
                          a.titre || tr('Chantier'),
                        )
                      : trf('Facture n° {0} en retard de {1} jour(s)', a.num, a.jours)}
                  </div>
                  <div style={{ fontSize: 9, color: C.muted }}>
                    {a.genre === 'finale'
                      ? tr('Touche pour la préparer, acomptes déduits')
                      : trf('{0} · touche pour relancer le client', a.titre || tr('Chantier'))}
                  </div>
                </div>
                <b style={{ fontSize: 11.5, color: a.genre === 'finale' ? C.amber : C.red }}>
                  {DEVISE} {fmt(a.montant)}
                </b>
              </div>
            ))}
          </div>
        </Card>
      )}
      {demandes.length > 0 && (
        <Card style={{ marginBottom: 12, border: '2px solid ' + C.teal }}>
          <Bar
            icon="🌴"
            title={'Congés, maladie, accident (' + demandes.length + ')'}
            color={C.teal}
            sub={
              peutValider
                ? 'À valider — approuve ou refuse'
                : 'En attente de validation par la direction'
            }
          />
          <div style={{ padding: '4px 13px 12px' }}>
            {demandes.map((c) => (
              <div key={c.id} style={{ borderBottom: '1px solid ' + C.border, padding: '12px 0' }}>
                <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: C.teal + '18',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15,
                      fontWeight: 900,
                      color: C.teal,
                      flexShrink: 0,
                    }}
                  >
                    {empName(c.empId).charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8,
                      }}
                    >
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>
                        {empName(c.empId)}
                      </div>
                      <Pill label="En attente" color={C.amber} />
                    </div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: C.grey,
                        marginTop: 3,
                        textTransform: 'capitalize',
                      }}
                    >
                      {c.type || 'congé'}
                    </div>
                    {/* Le certificat que l'ouvrier a envoyé, à un geste. */}
                    <div style={{ marginTop: 3 }}>
                      <LienCertificat conge={c} />
                    </div>
                    <div style={{ fontSize: 10.5, color: C.ink, marginTop: 2 }}>
                      📅 du <b>{fd(c.debut)}</b>
                      {c.fin && c.fin !== c.debut ? (
                        <>
                          {' '}
                          au <b>{fd(c.fin)}</b>
                        </>
                      ) : (
                        ''
                      )}{' '}
                      ·{' '}
                      <b>
                        {c.jours || 1} jour{(c.jours || 1) > 1 ? 's' : ''}
                      </b>
                    </div>
                  </div>
                </div>
                {peutValider ? (
                  <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                    <div style={{ flex: 1 }}>
                      <Btn
                        label="✓ Approuver"
                        color={C.green}
                        full
                        sm
                        onClick={() => setStatut(c.id, 'Approuvé')}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Btn
                        label="✕ Refuser"
                        color={C.red}
                        full
                        sm
                        onClick={() => setStatut(c.id, 'Refusé')}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Btn label="📅 Date" color={C.teal} full sm onClick={() => setProposeC(c)} />
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 8, fontStyle: 'italic' }}>
                    Seule la direction peut valider cette demande.
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
      {proposeC && (
        <ProposerDate
          conge={proposeC}
          nomEmploye={empName(proposeC.empId)}
          setConges={setConges}
          onClose={() => setProposeC(null)}
        />
      )}
      <div style={{ fontSize: 10.5, color: C.grey, marginBottom: 11, lineHeight: 1.5 }}>
        Signaux : offre ou facture ouverte par le client, demande de congé, devis accepté / refusé,
        acompte encaissé, facture payée. Mise à jour automatique une fois l'app reliée au serveur en
        ligne (clic du client, paiement reçu).
      </div>
      {evts.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: '44px 0', fontSize: 12 }}>
          🔕 Aucune autre notification pour l'instant.
        </div>
      ) : (
        evts.map((e, idx) => (
          <div
            key={idx}
            onClick={() => {
              // La pièce elle-même quand le signal en désigne une, comme pour
              // les consultations ; sinon la liste qui lui correspond.
              if (e.cible) return onNav(...e.cible);
              const t = e.txt || '';
              onNav(/Facture/.test(t) ? 'factures' : /Offre|Devis/.test(t) ? 'devis' : 'equipe');
            }}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              background: C.white,
              border: '1px solid ' + C.border,
              borderRadius: 11,
              padding: '12px 13px',
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 20 }}>{e.ic}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: C.ink }}>{e.txt}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{e.date}</div>
            </div>
            <span style={{ fontSize: 15, color: C.muted, flexShrink: 0 }}>›</span>
          </div>
        ))
      )}
    </Shell>
  );
}
